import React, { useState, useCallback, useEffect } from "react";
import { format } from "date-fns";
import Clipboard from "react-clipboard.js";
import { Segment, Header, List, Button, Grid, Modal } from "semantic-ui-react";
import { toast } from "react-semantic-toasts";

import { parseServerTimestampForClient } from "../../common/helpers";
import { formatAmount } from "utilities/dist/helpers";
import { DateFormat } from "utilities/dist/invoice/constants";
import { resetProductPriceToListPrice } from "./actions";

const ProductSingleComponent = ({ product, onEdit, customer }) => {
    const [openModal, setOpenModal] = useState(false);
    const [productPriceHistory, setProductPriceHistory] = useState(null);

    useEffect(() => {
        setProductPriceHistory(product.priceChanges || []);
    });

    const prices = [
        `List ${formatAmount(product.product_price)}`,
        product.buying_price ? `Buying ${formatAmount(product.buying_price)}` : "",
    ].filter(Boolean);
    const deets = [product.product_type, product.product_size].map(p => p.toUpperCase());

    const latestCustomerChangedPrice = productPriceHistory
        ?.filter(priceChange => Date.parse(priceChange.effective_date) <= new Date())
        .filter(
            priceChange =>
                priceChange.customer_id === customer?.id || priceChange.customer_id === null
        )
        .sort((a, b) => Date.parse(b.effective_date) - Date.parse(a.effective_date))?.[0];

    const isCurrentListPrice = latestCustomerChangedPrice
        ? latestCustomerChangedPrice.new_price === product.product_price
        : true;

    const closeModal = useCallback(async shouldReset => {
        if (shouldReset) {
            resetProductPriceToListPrice(product.id, customer.id).then(res => {
                setProductPriceHistory([...product.priceChanges, res.data]);
                product.priceChanges.push(res.data);
            });
        }
        setOpenModal(false);
    });

    return (
        <>
            <Segment attached="top" data-cy={`selected_product_details`}>
                <p>
                    <Clipboard
                        component="i"
                        data-clipboard-text={product.product}
                        button-className="copy icon clickable"
                        onSuccess={data => {
                            toast({
                                icon: "copy outline",
                                title: `Copied to clipboard`,
                                description: `${data.text} has been copied`,
                            });
                        }}
                    />{" "}
                    <b>{product.product}</b>
                    <br />
                    <Clipboard
                        component="i"
                        data-clipboard-text={product.upc}
                        button-className="copy icon clickable"
                        onSuccess={data => {
                            toast({
                                icon: "copy outline",
                                title: `Copied to clipboard`,
                                description: `${data.text} has been copied`,
                            });
                        }}
                    />{" "}
                    UPC: {product.upc}
                    <br />
                    {[...deets, ...prices].join(" | ")}
                </p>

                {productPriceHistory?.length > 0 && (
                    <>
                        <Header size="small" content="Price Changes" dividing />
                        <List relaxed>
                            {productPriceHistory.map(change => (
                                <Grid columns={2} key={`product_price_change_${change.id}`}>
                                    <Grid.Row>
                                        <Grid.Column>
                                            <List.Item
                                                data-cy={`product_price_change_${change.id}`}
                                            >
                                                <List.Header data-cy={`product_price_changes`}>
                                                    {formatAmount(change.old_price)} {"->"}{" "}
                                                    {formatAmount(change.new_price)}
                                                </List.Header>
                                                <List.Description>
                                                    Changed on{" "}
                                                    {format(
                                                        parseServerTimestampForClient(
                                                            change.created_at
                                                        ),
                                                        DateFormat
                                                    )}
                                                </List.Description>
                                                <List.Content>
                                                    Effective from{" "}
                                                    {format(
                                                        parseServerTimestampForClient(
                                                            change.effective_date
                                                        ),
                                                        DateFormat
                                                    )}
                                                </List.Content>
                                            </List.Item>
                                        </Grid.Column>
                                        <Grid.Column>
                                            {change.customer_id === customer?.id &&
                                                !isCurrentListPrice &&
                                                latestCustomerChangedPrice.new_price ===
                                                    change.new_price && (
                                                    <Button
                                                        onClick={() => setOpenModal(true)}
                                                        color="yellow"
                                                        style={{ color: "#000000" }}
                                                        className="text-sm text-right"
                                                        content="Revert to Product Catalog Price"
                                                        data-cy={`revert_product_price_button`}
                                                    />
                                                )}
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            ))}
                            <Modal
                                size="tiny"
                                open={openModal}
                                data-cy={`revert_product_price_modal`}
                            >
                                <Modal.Header data-cy={`revert_product_price_modal_header`}>
                                    Revert to Product Catalog Price
                                </Modal.Header>
                                <Modal.Content>
                                    <p data-cy={`revert_product_price_modal_text`}>
                                        Are you sure you want to change it to{" "}
                                        <b>Product Catalog Price</b>?
                                    </p>
                                </Modal.Content>
                                <Modal.Actions>
                                    <Button
                                        onClick={() => closeModal(false)}
                                        data-cy={`revert_product_price_modal_cancel`}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        positive
                                        onClick={() => closeModal(true)}
                                        data-cy={`revert_product_price_modal_confirm`}
                                    >
                                        Yes, Change It
                                    </Button>
                                </Modal.Actions>
                            </Modal>
                        </List>
                    </>
                )}
            </Segment>
            {!!onEdit && (
                <Button
                    icon="edit"
                    content="Edit"
                    attached="bottom"
                    onClick={() => onEdit(product)}
                />
            )}
        </>
    );
};

export default ProductSingleComponent;
